import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, mergeProps as _mergeProps, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "symptom-note-select-info" }
const _hoisted_2 = { class: "symptom-note-select-info-description" }
const _hoisted_3 = { class: "symptom-note-select-info-caution" }
const _hoisted_4 = { class: "symptom-note-select-contents" }
const _hoisted_5 = {
  key: 0,
  class: "symptom-note-select-error"
}
const _hoisted_6 = { class: "symptom-note-select-date-wrapper" }
const _hoisted_7 = {
  key: 1,
  class: "item-calendar-button"
}
const _hoisted_8 = { class: "item-calendar-date" }
const _hoisted_9 = {
  key: 3,
  class: "item-calendar-button"
}
const _hoisted_10 = { class: "symptom-note-select-list" }
const _hoisted_11 = ["id", "onUpdate:modelValue", "onChange"]
const _hoisted_12 = ["for"]
const _hoisted_13 = { class: "item-detail-wrapper" }
const _hoisted_14 = { class: "item-detail-text" }
const _hoisted_15 = { class: "item-bottom-wrapper" }
const _hoisted_16 = { class: "item-bottom-date" }
const _hoisted_17 = {
  key: 0,
  class: "symptom-note-select-error"
}
const _hoisted_18 = { class: "modal-footer-buttons" }

import dayjs from "dayjs";
import "dayjs/locale/ja";

import {
  onMounted,
  ref,
  reactive,
  computed,
} from "vue";
import { processing, processend } from "@/utils/view";
import { NoteData, NoteType } from "./types";

import Modal from "@/views/base/ModalComponent.vue";
import ClinicNote from "@/models/myRecord/clinicNote/clinicNote";
import ErrorDialog from "@/views/base/CommonMessage.vue";

// 日付のローカライズ
type itemData = {
  id: string;
  checked: boolean;
  content: string;
  date: string;
  categoryClass: string;
  categoryLabel: string;
};

const componentId = "healthcaredata02-01-f";

export default /*@__PURE__*/_defineComponent({
  __name: 'NoteSelector',
  props: {
    type: {},
    clinicNotes: {}
  },
  emits: ["close", "save"],
  setup(__props: any, { emit: __emit }) {

dayjs.locale("ja");

// --- Props ----------------------------------------
const props = __props;

// --- Types ------------------------------------------------------
const emit = __emit;

// --- Const / Variables ------------------------------------------
const clinicNotes = reactive(props.clinicNotes);
const currentYearMonth = ref(dayjs()); // 対象年月
const checkedSet = reactive(new Map<string, itemData>()); // チェック項目
const dailyNote = reactive({
  checkedCount: 0,
  currentItems: [] as itemData[],
  pastItems: [] as itemData[],
  prevMonth: "",
  nextMonth: "",
}); // 症状・こころノート

// --- Dialogs ----------------------------------------------------

// エラーダイアログ関連情報
const sessionErr = ref(false);
let errorDialogSet = reactive({
  show: false,
  okButtonHide: true,
  safe: false,
  message: "",
  open: (message: string): void => {
    processing(); // オーバレイを表示
    errorDialogSet.message = message;
    errorDialogSet.show = true;
  },
  close: (): void => {
    processend(); // オーバレイを非表示
    errorDialogSet.show = false;
    errorDialogSet.message = "";
    if (sessionErr.value) {
      window.location.replace("/cms/login");
    }
  },
});

// --- Event Handlers ------------------------------------------------

// 前ページ
async function onPrev() {
  currentYearMonth.value = dayjs(dailyNote.prevMonth);
  await set();
}

// 次ページ
async function onNext() {
  currentYearMonth.value = dayjs(dailyNote.nextMonth);
  await set();
}

// チェック制御
function onCheck(item: itemData) {
  const id = item.id;
  const count = checkedSet.size;

  if (!checkedSet.has(id)) {
    if (count >= 10 - registeredCount.value) {
      item.checked = false;
      return;
    }
    checkedSet.set(id, item);
    item.checked = true;
  } else {
    checkedSet.delete(id);
    item.checked = false;
  }

  dailyNote.checkedCount = checkedSet.size;
}

// キャンセル
function onClose() {
  emit("close");
}

// 保存
function onSave() {
  const selectedNotes = Array.from(checkedSet.values());

  // マージ後の合計が10を超える場合は、空の入力欄を削除して枠を確保
  let base = [...clinicNotes];
  if (base.length + selectedNotes.length > 10) {
    base = base.filter((note) => (note.content || "").trim().length > 0);
  }

  const remaining = Math.max(0, 10 - base.length);
  const notesToAppend = selectedNotes.slice(0, remaining).map((note) => ({
    input_date: note.date,
    content: note.content,
    sub_number: 0,
  }));
  emit("save", props.type as NoteType, [...base, ...notesToAppend]);
}

// --- Functions ----------------------------------------------------

// 日付フォーマット
function formatDate(date: dayjs.Dayjs | string | null, fmt: string) {
  return date ? dayjs(date).format(fmt) : "";
}

// 表示設定
async function set(initial = false, checkedCount = 0) {
  // 症状・こころノートの取得
  const clinicNoteModel = new ClinicNote();
  const [_model, response, err] = await clinicNoteModel.getDailyNote({
    initial: initial,
    type: props.type,
    checkedCount: checkedCount,
    month: currentYearMonth.value.format("YYYY-MM"),
  });

  // エラー判定
  if (err) {
    const status = err.error.response.status;
    const decodedMessage = decodeURIComponent(
      err.error?.response?.data?.message
    );
    // セッションエラー
    if (status === 408) sessionErr.value = true;
    errorDialogSet.open(decodedMessage);
    return;
  }

  // 初回だけBEのチェック項目を使う
  // ※ 以降はFEで管理している項目を使う
  if (initial) {
    const items = [
      ...response.data.currentItems.filter((i: any) => i.checked),
      ...response.data.pastItems.filter((i: any) => i.checked),
    ];

    checkedSet.clear();
    for (const item of items) {
      checkedSet.set(item.id, item);
    }
  }

  // データ更新
  dailyNote.prevMonth = response.data.prevMonth;
  dailyNote.nextMonth = response.data.nextMonth;
  dailyNote.currentItems = response.data.currentItems.map((item: any) => ({
    ...item,
    checked: checkedSet.has(item.id),
  }));

  dailyNote.pastItems = response.data.pastItems.map((item: any) => ({
    ...item,
    checked: checkedSet.has(item.id),
  }));
  dailyNote.checkedCount = checkedSet.size;
}

// --- Vue Lifecycle Hooks --------------------------------------------
onMounted(async () => await set(true, registeredCount.value));
// テキストが1文字以上ある行のみカウント（空の入力欄は選択上限に含めない）
const registeredCount = computed(
  () => clinicNotes.filter((n) => (n.content || "").trim().length > 0).length
);
const remainingSelectableCount = computed(() =>
  Math.max(0, 10 - registeredCount.value)
);
const hasCheckedPast = computed(() => {
  const currentMonth = currentYearMonth.value.format("YYYY-MM");

  return Array.from(checkedSet.values()).some((item) => {
    const itemMonth = dayjs(item.date).format("YYYY-MM");
    return itemMonth !== currentMonth;
  });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Modal, {
      id: componentId,
      title: _ctx.$t('clinic_note_note_selector_title'),
      showTab: false,
      modalClass: "modal-symptom-note-select",
      onClose: onClose
    }, {
      bodyInfo: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t("clinic_note_note_selector_description")), 1),
          _createElementVNode("p", _hoisted_3, [
            _createTextVNode(_toDisplayString(_ctx.$t("clinic_note_note_selector_caution", {
              count: remainingSelectableCount.value,
            })) + " ", 1),
            _cache[0] || (_cache[0] = _createElementVNode("br", { class: "pc-only" }, null, -1)),
            _createTextVNode(_toDisplayString(_ctx.$t("clinic_note_note_selector_caution2")), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          (hasCheckedPast.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_ctx.$t("clinic_note_note_selector_error")), 1))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_6, [
            (dailyNote?.prevMonth)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "item-calendar-button",
                  onClick: onPrev
                }, _cache[1] || (_cache[1] = [
                  _createElementVNode("img", { src: "/server/necs/images/arrow_left_888.svg" }, null, -1)
                ])))
              : (_openBlock(), _createElementBlock("button", _hoisted_7, _cache[2] || (_cache[2] = [
                  _createElementVNode("img", { src: "/server/necs/images/arrow_left_ddd.svg" }, null, -1)
                ]))),
            _createElementVNode("p", _hoisted_8, [
              _createElementVNode("span", null, _toDisplayString(formatDate(currentYearMonth.value, "YYYY")) + "年", 1),
              _createTextVNode(_toDisplayString(formatDate(currentYearMonth.value, "M")) + "月 ", 1)
            ]),
            (dailyNote?.nextMonth)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 2,
                  class: "item-calendar-button",
                  onClick: onNext
                }, _cache[3] || (_cache[3] = [
                  _createElementVNode("img", { src: "/server/necs/images/arrow_right_888.svg" }, null, -1)
                ])))
              : (_openBlock(), _createElementBlock("button", _hoisted_9, _cache[4] || (_cache[4] = [
                  _createElementVNode("img", { src: "/server/necs/images/arrow_right_ddd.svg" }, null, -1)
                ])))
          ]),
          _createElementVNode("ul", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dailyNote?.currentItems, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: item.id,
                class: "symptom-note-select-list-item"
              }, [
                _withDirectives(_createElementVNode("input", {
                  type: "checkbox",
                  id: 'symptom-note-select-list-item-' + item.id,
                  "onUpdate:modelValue": ($event: any) => ((item.checked) = $event),
                  onChange: ($event: any) => (onCheck(item))
                }, null, 40, _hoisted_11), [
                  [_vModelCheckbox, item.checked]
                ]),
                _createElementVNode("label", {
                  for: 'symptom-note-select-list-item-' + item.id
                }, [
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("p", _hoisted_14, _toDisplayString(item.content), 1),
                    _createElementVNode("div", _hoisted_15, [
                      _createElementVNode("span", _hoisted_16, _toDisplayString(formatDate(item.date, "YYYY年M月D日(ddd)")), 1),
                      _createElementVNode("div", {
                        class: _normalizeClass(["item-bottom-category", item.categoryClass])
                      }, _toDisplayString(item.categoryLabel), 3)
                    ])
                  ])
                ], 8, _hoisted_12)
              ]))
            }), 128))
          ])
        ])
      ]),
      footerHeader: _withCtx(() => [
        (registeredCount.value + checkedSet.size >= 10)
          ? (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString(_ctx.$t("clinic_note_note_selector_error2")), 1))
          : _createCommentVNode("", true)
      ]),
      footerButton: _withCtx(() => [
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("button", {
            onClick: onClose,
            type: "button",
            class: "modal-cansel"
          }, _toDisplayString(_ctx.$t("clinic_note_multi_edit_button_cancel")), 1),
          _createElementVNode("button", {
            onClick: onSave,
            type: "button",
            class: "modal-ok"
          }, _toDisplayString(_ctx.$t("clinic_note_multi_edit_button_add")), 1)
        ])
      ]),
      _: 1
    }, 8, ["title"]),
    (_unref(errorDialogSet).show)
      ? (_openBlock(), _createBlock(ErrorDialog, _mergeProps({ key: 0 }, _unref(errorDialogSet), {
          onOk: _unref(errorDialogSet).close,
          onCancel: _unref(errorDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})